/**
 * DRPX Courier — In-App-Benachrichtigungen (Supabase)
 *
 * CRUD für die `notifications`-Tabelle und E-Mail-Versand via
 * `send-email` Edge Function (Resend). Wird von `useNotifications.js`
 * als einzigem Caller konsumiert.
 *
 * Mehrere Funktionen (`markNotifRead`, `markAllNotifsRead`, `pushNotif`)
 * sind bewusst fire-and-forget — Fehler werden ignoriert, weil
 * UI-Konsistenz wichtiger ist als strikte Fehlerbehandlung.
 *
 * @module lib/supabase/notifications
 */

import { NOTIF_DEFAULTS } from '../../config/constants.js';
import { _sb } from './client.js';
import { mapNotificationFromDb } from './mappers.js';

// ════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt die letzten 50 Benachrichtigungen eines Users für die Glocke im
 * Header.
 */
export async function getNotifications(userId: string): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: new Error('Supabase client not initialized') };
  const client = _sb;
  try {
    const r = await client
      .from('notifications')
      .select('*')
      .eq('empfaenger_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapNotificationFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Markiert eine einzelne Benachrichtigung als gelesen. Fehler werden
 * stillschweigend ignoriert, weil UI-Konsistenz hier wichtiger ist als
 * Fehlerbehandlung.
 */
export async function markNotifRead(id: string): Promise<void> {
  if (!_sb) return;
  const client = _sb;
  try {
    await client.from('notifications').update({ read: true }).eq('id', id);
  } catch {
    /* ignored */
  }
}

/**
 * Markiert alle ungelesenen Benachrichtigungen eines Users als gelesen
 * ("alles gelesen"-Button).
 */
export async function markAllNotifsRead(userId: string): Promise<void> {
  if (!_sb) return;
  const client = _sb;
  try {
    await client
      .from('notifications')
      .update({ read: true })
      .eq('empfaenger_id', userId)
      .eq('read', false);
  } catch {
    /* ignored */
  }
}

/**
 * Legt eine neue Benachrichtigung für einen Empfänger an. Der Sender wird
 * automatisch aus der aktuellen Session gelesen (wichtig für RLS).
 * Anonyme Pushes werden verworfen.
 */
export async function pushNotif(
  empfaengerId: string,
  title: string,
  sub?: string,
  tab?: string,
): Promise<void> {
  if (!_sb) return;
  const client = _sb;
  try {
    // sender_id automatisch aus aktueller Session (für RLS-Check)
    const sessRes = await client.auth.getSession();
    const senderId = sessRes?.data?.session?.user?.id || null;
    if (!senderId) return; // Kein anonymer push
    await client.from('notifications').insert({
      empfaenger_id: empfaengerId,
      sender_id: senderId,
      title: title,
      sub: sub || '',
      tab: tab || null,
    });
  } catch {
    /* ignored */
  }
}

// ════════════════════════════════════════════════════════════════════
// EMAIL NOTIFICATIONS
// ════════════════════════════════════════════════════════════════════

/** Ein passender Empfänger für einen neu ausgeschriebenen Auftrag. */
export interface MatchingFahrer {
  id: string;
  lang: string;
  /** Kann dieser Fahrer den Auftrag überhaupt annehmen? */
  hatZugang: boolean;
  /**
   * Warum er passt (Migration 123):
   * - 'gebiet'     — Einsatzgebiet im Profil → Meldung „Neuer passender Auftrag"
   *                  (Fahrer: Profil-Fahrzeug; Spedition seit Migration 128:
   *                  mindestens ein passendes Fahrzeug im Fuhrpark)
   * - 'rueckfahrt' — gemerkte Strecke → Meldung „Passt zu deiner Rückfahrt"
   * Passt beides, liefert die Datenbank 'gebiet'.
   *
   * Das frühere Feld `pref` (roher Einstellungswert) ist am 20.09.2026
   * entfallen: Es wurde nie gelesen — `pushNotif` holt die Einstellung je
   * Kategorie selbst über `get_notif_delivery`.
   */
  quelle: 'gebiet' | 'rueckfahrt';
}

/**
 * Passende Fahrer und Speditionen für einen neuen Auftrag (Migration 120,
 * erweitert in 123/124 um gemerkte Strecken — siehe `MatchingFahrer.quelle` —
 * und in 128 um Speditionen mit Einsatzgebiet und Fuhrpark). Pro Empfänger
 * höchstens EINE Zeile, also höchstens eine Meldung pro Auftrag.
 *
 * Das Matching lief bisher im Browser über `users.filter(...)` und fand NIE
 * jemanden: Die Policy `profiles_select_own_or_admin` gibt einem normalen
 * Nutzer nur das eigene Profil frei. Die Meldung „Neuer passender Auftrag"
 * wurde deshalb noch nie verschickt.
 *
 * Geprüft wird serverseitig: Einsatzgebiet mit Radius, Fahrzeug nach
 * Größenleiter (vom Empfänger über `notifPrefs.fahrzeugFilter` steuerbar)
 * und Ausstattung. Abgeschaltete Empfänger sind gar nicht erst dabei.
 * Zurück kommen weder Adressen noch Namen.
 *
 * Nur der Auftraggeber darf abfragen — sonst könnte jeder die Fahrerschaft
 * nach Einsatzgebieten durchprobieren.
 *
 * Bei einem Fehler kommt eine leere Liste: Der Auftrag ist dann trotzdem
 * angelegt, es geht nur keine Meldung raus. Eine fehlende Benachrichtigung
 * darf das Ausschreiben nicht scheitern lassen.
 */
export async function getMatchingFahrer(auftragId: string): Promise<MatchingFahrer[]> {
  if (!_sb || !auftragId) return [];
  const client = _sb;
  try {
    const r = await client.rpc('get_matching_fahrer', { p_auftrag_id: auftragId });
    if (r.error) {
      console.warn('getMatchingFahrer:', r.error.message);
      return [];
    }
    const rows = (Array.isArray(r.data) ? r.data : []) as Array<{
      fahrer_id?: string;
      lang?: string;
      hat_zugang?: boolean;
      quelle?: string;
    }>;
    return rows
      .filter((row) => !!row.fahrer_id)
      .map((row) => ({
        id: row.fahrer_id as string,
        lang: typeof row.lang === 'string' && row.lang ? row.lang : 'de',
        hatZugang: row.hat_zugang === true,
        // Alles außer ausdrücklich 'rueckfahrt' gilt als Einsatzgebiet —
        // so verhält sich der Client auch gegenüber einer Datenbank ohne
        // Migration 123 wie bisher.
        quelle: row.quelle === 'rueckfahrt' ? 'rueckfahrt' : 'gebiet',
      }));
  } catch (e: unknown) {
    console.warn('getMatchingFahrer:', e instanceof Error ? e.message : String(e));
    return [];
  }
}

/**
 * Benachrichtigt alle Admins (Migration 118).
 *
 * Der Browser kann Admins nicht selbst finden: Die Policy
 * `profiles_select_own_or_admin` gibt einem normalen Nutzer nur das eigene
 * Profil frei, `users.filter(u => u.rolle === 'admin')` blieb deshalb immer
 * leer. Meldungen wie „neues Zertifikat" oder „Verifizierungs-Dokument
 * hochgeladen" kamen nie an.
 *
 * Die Funktion legt die Glocken-Benachrichtigungen serverseitig an und gibt
 * die Kennungen der Admins zurück, die zusätzlich eine E-Mail möchten. Diese
 * Mails werden hier direkt ausgelöst — die Adresse bleibt dabei
 * serverseitig, `send-email` löst sie über die Kennung auf.
 *
 * Schlägt der Aufruf fehl, passiert nichts weiter: Die auslösende Aktion
 * (Upload, Registrierung) ist davon unberührt und gilt weiterhin als
 * erfolgreich. Eine fehlende Benachrichtigung darf sie nicht scheitern
 * lassen.
 */
export async function notifyAdmins(
  title: string,
  sub: string,
  tab?: string,
  kategorie?: string,
): Promise<void> {
  if (!_sb || !title) return;
  const client = _sb;
  try {
    // Den Standardwert gibt der Aufrufer mit (Migration 119). Die Datenbank
    // rät nicht — sonst lägen die Standardwerte an zwei Orten und liefen
    // auseinander. Genau das war in Migration 118 passiert: dort stand fest
    // 'app', während NOTIF_DEFAULTS für neuesZertifikat 'both' vorsieht.
    const standard = (kategorie && NOTIF_DEFAULTS[kategorie]) || 'app';
    const r = await client.rpc('notify_admins', {
      p_title: title,
      p_sub: sub || '',
      p_tab: tab ?? null,
      p_kategorie: kategorie ?? null,
      p_standard: standard,
    });
    if (r.error) {
      console.warn('notifyAdmins:', r.error.message);
      return;
    }
    const rows = (Array.isArray(r.data) ? r.data : []) as Array<{ email_empfaenger?: string }>;
    for (const row of rows) {
      if (!row.email_empfaenger) continue;
      void sendEmailNotifToUser(
        row.email_empfaenger,
        'DRPX Courier: ' + title,
        title + '\n\n' + (sub || ''),
      );
    }
  } catch (e: unknown) {
    console.warn('notifyAdmins:', e instanceof Error ? e.message : String(e));
  }
}

/**
 * Zustellart und Sprache eines Empfängers (Migration 117).
 *
 * Beides steht im Profil des EMPFÄNGERS, das ein normaler Nutzer nicht
 * lesen darf. Die SECURITY-DEFINER-Funktion gibt genau diese zwei Angaben
 * heraus — keine Adresse, keinen Namen.
 *
 * `pref` ist der ROHE Wert aus `notif_prefs` oder `null`, wenn der Nutzer
 * nichts eingestellt hat. Die Standardwerte je Kategorie bleiben bewusst im
 * Frontend (NOTIF_DEFAULTS), damit sie nicht an zwei Orten liegen.
 *
 * Fällt der Aufruf aus (Funktion fehlt, Netz weg), wird `null` für `pref`
 * und `'de'` geliefert — also das bisherige Verhalten. Eine Benachrichtigung
 * geht damit im Zweifel raus statt verloren.
 */
export async function getNotifDelivery(
  empfaengerId: string,
  kategorie?: string,
): Promise<{ pref: string | null; lang: string }> {
  const fallback = { pref: null, lang: 'de' };
  if (!_sb || !empfaengerId) return fallback;
  const client = _sb;
  try {
    const r = await client.rpc('get_notif_delivery', {
      p_empfaenger: empfaengerId,
      p_kategorie: kategorie ?? null,
    });
    if (r.error) {
      console.warn('getNotifDelivery:', r.error.message);
      return fallback;
    }
    const row = Array.isArray(r.data) ? r.data[0] : r.data;
    if (!row) return fallback;
    const rec = row as { pref?: string | null; lang?: string | null };
    return {
      pref: typeof rec.pref === 'string' ? rec.pref : null,
      lang: typeof rec.lang === 'string' && rec.lang ? rec.lang : 'de',
    };
  } catch (e: unknown) {
    console.warn('getNotifDelivery:', e instanceof Error ? e.message : String(e));
    return fallback;
  }
}

/**
 * Benachrichtigungs-Mail an einen Empfänger, der nur über seine Kennung
 * bekannt ist.
 *
 * Hintergrund (01.09.2026): Ein normaler Nutzer darf das Profil des
 * Empfängers nicht lesen — die Policy `profiles_select_own_or_admin` gibt
 * nur das eigene frei. Der Client kennt die Adresse also gar nicht. Genau
 * daran scheiterte der Versand bisher stillschweigend: In `pushNotif` blieb
 * die Suche nach dem Empfänger ohne Treffer, und es ging NIE eine
 * Benachrichtigungs-Mail raus.
 *
 * Die Edge Function `send-email` löst die Adresse mit Dienstrechten selbst
 * auf. Sie verwirft dabei eine eventuell mitgeschickte Adresse — sonst
 * könnte jemand eine fremde Kennung mit der eigenen Adresse kombinieren.
 *
 * Für den Admin-Bereich bleibt `sendEmailNotif` mit direkter Adresse
 * bestehen; dort ist sie bekannt und legitim.
 *
 * @param empfaengerId - Profil-Kennung des Empfängers.
 */
export async function sendEmailNotifToUser(
  empfaengerId: string,
  subject: string,
  body: string,
): Promise<{ data?: unknown; error?: string }> {
  if (!_sb) return { error: 'Supabase client not initialized' };
  if (!empfaengerId) return { error: 'empfaengerId fehlt' };
  const client = _sb;
  try {
    const res = await client.functions.invoke('send-email', {
      body: JSON.stringify({ to_user_id: empfaengerId, subject: subject, body: body }),
    });
    if (res.error) console.warn('Email send error:', res.error);
    return { data: res.data };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('Email function not deployed:', msg);
    return { error: msg };
  }
}

/**
 * Schickt eine Transaktions-E-Mail über die `send-email` Edge Function
 * (die intern Resend verwendet). Fehler werden nur geloggt, nicht geworfen —
 * die App soll bei E-Mail-Problemen nicht abbrechen.
 */
export async function sendEmailNotif(
  toEmail: string,
  subject: string,
  body: string,
): Promise<{ data?: unknown; error?: string }> {
  if (!_sb) return { error: 'Supabase client not initialized' };
  const client = _sb;
  try {
    const res = await client.functions.invoke('send-email', {
      body: JSON.stringify({ to: toEmail, subject: subject, body: body }),
    });
    if (res.error) console.warn('Email send error:', res.error);
    return { data: res.data };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('Email function not deployed:', msg);
    return { error: msg };
  }
}
